import {
  Box,
  Stack,
  Button,
  Heading,
  Space,
  InlineLoader,
  useIsMobileLayout,
} from "@dbn/omnichannel-design-system-g4p";
import { constFalse, pipe } from "fp-ts/function";
import { array, boolean, option, taskEither } from "fp-ts";
import { DocumentMeta } from "../../Common/documentAPI";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import { useFormatMessage } from "../../intl";
import { useCommand } from "../../useAPI";
import * as documentAPI from "../../Common/documentAPI";
import { Option } from "fp-ts/Option";
import { useState } from "react";
import { PdfIcon } from "@g4p/react-styleguide-library";
import { useDownloadDocumentToDevice } from "../../Common/useDownloadDocument";

type Props = {
  documents: NonEmptyArray<DocumentMeta>;
};

export function Contracts(props: Props) {
  const documentDownload = useCommand(documentAPI.content);
  const downloadDocumentToDevice = useDownloadDocumentToDevice();

  const [contractDownloading, setContractDownloading] = useState<
    Option<DocumentMeta>
  >(option.none);

  const Document = ({ contract }: { contract: DocumentMeta }) => {
    const formatMessage = useFormatMessage();
    const isMobileLayout = useIsMobileLayout();

    const renderDownloadDocument = () =>
      pipe(
        contractDownloading,
        option.fold(constFalse, ({ docId }) => docId === contract.docId),
        boolean.fold(
          () => (
            <Button
              key={contract.docId}
              label={formatMessage(
                "SummaryAndDownload.Summary.contracts.download.button"
              )}
              variant="text"
              size="default"
              action={pipe(
                taskEither.bracket(
                  taskEither.rightIO(() =>
                    setContractDownloading(option.some(contract))
                  ),
                  () =>
                    pipe(
                      documentDownload({ docId: contract.docId }),
                      taskEither.map(downloadDocumentToDevice)
                    ),
                  () =>
                    taskEither.rightIO(() =>
                      setContractDownloading(option.none)
                    )
                )
              )}
            />
          ),
          () => (
            <InlineLoader
              message={formatMessage(
                "SummaryAndDownload.Summary.contracts.download.loading"
              )}
              size="medium"
            />
          )
        )
      );

    return (
      <Box column={isMobileLayout} key={contract.docId} vAlignContent="center">
        <Box vAlignContent="center">
          <Box hAlignContent="left" vAlignContent="center">
            <PdfIcon width={14} height={14} />
          </Box>
          <Space units={2} />
          <Box grow>
            <Heading size="x-small" weight="regular">
              {contract.docTitle}
            </Heading>
          </Box>
        </Box>
        {renderDownloadDocument()}
      </Box>
    );
  };

  return (
    <Box column>
      <Stack column units={4}>
        {pipe(
          props.documents,
          array.map(contract => (
            <Document key={contract.docId} contract={contract} />
          ))
        )}
      </Stack>
    </Box>
  );
}
